import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { api } from '../lib/api';
import { tokenStore } from '../lib/tokenStore';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [walkAsUser, setWalkAsUser] = useState(null);
  const [token, setToken] = useState(tokenStore.getToken());
  const [loading, setLoading] = useState(true);

  const isWalkingAs = !!walkAsUser;

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const savedWalkAs = tokenStore.getWalkAsUser();
    const savedOriginal = tokenStore.getOriginalUser();
    if (savedWalkAs && savedOriginal) {
      setWalkAsUser(savedWalkAs);
      setOriginalUser(savedOriginal);
      setUser(savedWalkAs);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const savedWalkAs = tokenStore.getWalkAsUser();
      if (savedWalkAs) {
        setOriginalUser(response.data);
      } else {
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    if (access_token) tokenStore.setToken(access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, full_name, role = 'end_user') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email, password, full_name, role
    });
    const { access_token, user: userData } = response.data;
    if (access_token) tokenStore.setToken(access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      const t = tokenStore.getToken();
      await axios.post(`${API_URL}/api/auth/logout`, {}, t ? { headers: { Authorization: `Bearer ${t}` } } : {});
    } catch (e) {
      // Ignore logout API errors
    }
    tokenStore.clearAll();
    setToken(null);
    setUser(null);
    setWalkAsUser(null);
    setOriginalUser(null);
  };

  const startWalkAs = async (targetUser) => {
    setOriginalUser(user);
    tokenStore.setOriginalUser(user);
    setWalkAsUser(targetUser);
    tokenStore.setWalkAsUser(targetUser);
    setUser(targetUser);
    try {
      await api.createAuditLog({
        action: 'walk_as_start',
        target_user_id: targetUser.id,
        target_user_name: targetUser.full_name,
        target_user_email: targetUser.email,
        target_user_role: targetUser.role,
        details: { session_start: new Date().toISOString() }
      });
    } catch (error) {
      console.error('Failed to create audit log:', error);
    }
  };

  const exitWalkAs = async () => {
    const exitedUser = walkAsUser;
    setUser(originalUser);
    setWalkAsUser(null);
    setOriginalUser(null);
    tokenStore.setWalkAsUser(null);
    tokenStore.setOriginalUser(null);
    if (exitedUser) {
      try {
        await api.createAuditLog({
          action: 'walk_as_end',
          target_user_id: exitedUser.id,
          target_user_name: exitedUser.full_name,
          target_user_email: exitedUser.email,
          target_user_role: exitedUser.role,
          details: { session_end: new Date().toISOString() }
        });
      } catch (error) {
        console.error('Failed to create audit log:', error);
      }
    }
  };

  const canWalkAs = originalUser?.role === 'admin' || originalUser?.role === 'super_admin' || 
                    (!walkAsUser && (user?.role === 'admin' || user?.role === 'super_admin'));

  return (
    <AuthContext.Provider value={{ 
      user, token, login, register, logout, loading,
      isWalkingAs, walkAsUser, originalUser, startWalkAs, exitWalkAs, canWalkAs
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
